import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { SubCategoryDto } from '../types/categoryTypes';
import { iconUrl } from '../config';
import '../styles/CategoryCard.css';

interface SubCategoryCardProps {
  subCategory: SubCategoryDto;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  department: string;
  isAdmin?: boolean;
  parentId?: number;
}

export function SubCategoryCard({
  subCategory,
  onEdit,
  onDelete,
  department,
  isAdmin = false,
  parentId,
}: SubCategoryCardProps): JSX.Element {
  const navigate = useNavigate();
  const displayName = subCategory.nameEn ?? subCategory.nameHe;
  const imgSrc = iconUrl(subCategory.iconUrl);

  function productsPath(): string {
    const params = new URLSearchParams();
    if (department) params.set('department', department);
    if (parentId)   params.set('parentId', String(parentId));
    const qs = params.toString();
    return `/categories/${subCategory.id}/products${qs ? `?${qs}` : ''}`;
  }

  const hasProducts = subCategory.productCount > 0;

  return (
    <div
      className={`subcat-chip${hasProducts ? '' : ' subcat-chip--empty'}`}
      onClick={hasProducts ? () => navigate(productsPath()) : undefined}
      title={hasProducts ? `צפה במוצרים של ${subCategory.nameHe}` : subCategory.nameHe}
      role={hasProducts ? 'button' : undefined}
      tabIndex={hasProducts ? 0 : undefined}
      onKeyDown={hasProducts ? (e) => { if (e.key === 'Enter' || e.key === ' ') navigate(productsPath()); } : undefined}
      aria-label={subCategory.nameHe}
    >
      {/* Icon */}
      {imgSrc ? (
        <img src={imgSrc} alt={displayName} className="subcat-chip__icon" />
      ) : (
        <div className="subcat-chip__icon-placeholder" aria-hidden="true" />
      )}

      {/* Name */}
      <span className="subcat-chip__name">{subCategory.nameHe}</span>

      {/* Edit / Delete — admin only, visible on hover */}
      {isAdmin && (
        <span className="subcat-chip__actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="subcat-chip__btn"
            onClick={() => onEdit(subCategory.id)}
            title="ערוך"
            aria-label={`ערוך ${subCategory.nameHe}`}
          >✏️</button>
          <button
            className="subcat-chip__btn subcat-chip__btn--delete"
            onClick={() => onDelete(subCategory.id)}
            title="מחק"
            aria-label={`מחק ${subCategory.nameHe}`}
          >🗑️</button>
        </span>
      )}
    </div>
  );
}
